import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ChevronRight,
  EyeIcon,
  PartyPopper,
  CheckCircle,
} from "lucide-react";
import {
  gradeFinishedStudentsOpenEndedAnswers,
  setStudentAssignmentToTeacherGraded,
  useGetFinishedOpenEndedQuestionsOfAssignment,
} from "@packages/api/madlenAPI";
import {
  OpenEndedAnswerWithQuestionDto,
  BaseOpenEndedQuestionDto,
} from "@packages/api/madlenAPI.schemas";
import { Button, Text } from "@core/index";
import Modal from "@core/modal";
import Markdown from "react-markdown";

const OpenEndedQuestionsReviewSequentialView = () => {
  const { assignmentId, studentId: studentIdParam } = useParams<{
    assignmentId: string;
    studentId: string;
  }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [studentId, setStudentId] = useState<number>(
    studentIdParam ? Number(studentIdParam) : -1,
  );
  const [score, setScore] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const { data, isLoading, error, refetch } =
    useGetFinishedOpenEndedQuestionsOfAssignment(Number(assignmentId), {
      query: {
        refetchOnWindowFocus: false,
      },
    });

  // Get current student and their questions
  const currentStudent = data?.studentAssignments?.find(
    (s) => s.studentId === Number(studentId),
  );
  const currentQuestions = currentStudent?.questionsWithStudentAnswers?.filter(
    (q) => q.type === "OE",
  ) as OpenEndedAnswerWithQuestionDto[] | undefined;
  const currentQuestion = currentQuestions?.[currentQuestionIndex];

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight + 10}px`;
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedback(e.target.value);
    adjustTextareaHeight(e.target);
  };

  useEffect(() => {
    if (currentQuestion) {
      setScore(currentQuestion.score || 0);
      setFeedback(currentQuestion.feedback || "");
      setShowAnswers(false);

      setTimeout(() => {
        const textarea = document.querySelector("textarea");
        if (textarea) {
          adjustTextareaHeight(textarea);
        }
      }, 0);
    }
  }, [currentQuestion]);

  useEffect(() => {
    if (studentIdParam) {
      setStudentId(Number(studentIdParam));
    } else {
      setStudentId(data?.studentAssignments?.[0]?.studentId ?? -1);
    }
  }, [studentIdParam, JSON.stringify(data)]);

  const handleBack = () => {
    navigate(`/assignments/${assignmentId}/open-ended-review`);
  };

  const moveToNextQuestion = async () => {
    if (!currentQuestions) return;

    if (currentQuestionIndex < currentQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else if (
      (data?.studentAssignments?.findIndex(
        (s) => s.studentId === Number(studentId),
      ) ?? 0) <
        (data?.studentAssignments?.length || 0) - 1 &&
      currentStudent?.studentId
    ) {
      await setStudentAssignmentToTeacherGraded(Number(assignmentId), [
        currentStudent?.studentId!,
      ]);
      const nextStudentId =
        data?.studentAssignments?.[
          data?.studentAssignments?.findIndex(
            (s) => s.studentId === Number(studentId),
          ) + 1
        ]?.studentId;
      navigate(
        `/assignments/${assignmentId}/open-ended-review/sequential/${nextStudentId}`,
      );
      setCurrentQuestionIndex(0);
      refetch();
    } else {
      setShowSuccessModal(true);
    }
  };

  const handleGradeSubmit = async () => {
    if (!currentQuestion || !currentStudent?.studentId) return;

    setIsSubmitting(true);
    try {
      await gradeFinishedStudentsOpenEndedAnswers(Number(assignmentId), {
        questionId: currentQuestion.questionId,
        studentId: currentStudent.studentId,
        score: score || 0,
        feedback,
      });
      moveToNextQuestion();
      refetch();
    } catch (error) {
      console.error("Error submitting grade:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-32 bg-gray-200 dark:bg-gray-700 rounded"
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data || !currentStudent || !currentQuestion) {
    return (
      <div className="p-6">
        <Text variant="h2" className="text-red-500">
          {t("assignments.openEnded.review.error")}
        </Text>
        <Button onClick={handleBack} className="mt-4">
          {t("common.back")}
        </Button>
      </div>
    );
  }

  const question = currentQuestion.question as BaseOpenEndedQuestionDto;
  const studentAnswer = currentQuestion.studentAnswer || "";
  const maxPoints = question.point || 0;

  return (
    <div className="p-4 mx-auto">
      {/* Main Content */}
      <div className="flex">
        {/* Students Progress Bar */}
        <div className="relative min-w-[60px]">
          <div className="absolute left-1/2 h-full w-0.5 bg-gray-200 dark:bg-gray-700 transform -translate-x-1/2" />
          <div className="relative space-y-4">
            {data?.studentAssignments?.map((student, index) => (
              <div key={index} className="relative">
                <div
                  className={`group flex items-center justify-center cursor-pointer transition-all`}
                  onClick={() => {
                    navigate(
                      `/assignments/${assignmentId}/open-ended-review/sequential/${student.studentId}`,
                    );
                    setCurrentQuestionIndex(0);
                  }}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      student.questionsWithStudentAnswers?.every(
                        (q) => q.didTeacherGrade,
                      )
                        ? "bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-200"
                    } ${
                      student.studentId === Number(studentId)
                        ? "border border-gray-500"
                        : ""
                    }`}
                  >
                    <span className="text-sm font-medium">
                      {student.studentName?.[0]}
                    </span>
                  </div>
                  {/* Hover tooltip */}
                  <div className="absolute left-full ml-2 scale-0 group-hover:scale-100 transition-transform origin-left z-50">
                    <div className="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-1 rounded-md text-sm whitespace-nowrap">
                      {student.studentName} {student.studentSurname}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Question Progress Bar */}
        <div className="relative min-w-[60px]">
          <div className="absolute left-1/2 h-full w-0.5 bg-gray-200 dark:bg-gray-700 transform -translate-x-1/2" />
          <div className="relative space-y-4">
            {currentQuestions?.map((question, index) => (
              <div
                key={index}
                className={`flex items-center justify-center cursor-pointer transition-all ${
                  index === currentQuestionIndex ? "scale-110" : ""
                }`}
                onClick={() => setCurrentQuestionIndex(index)}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    question.didTeacherGrade
                      ? "bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-200"
                  } ${
                    index === currentQuestionIndex
                      ? "border border-gray-500"
                      : ""
                  }`}
                >
                  {question.didTeacherGrade ? (
                    <div className="flex flex-col items-center">
                      <CheckCircle className="w-3 h-3" />
                      <span className="text-xs">{index + 1}</span>
                    </div>
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 space-y-2">
          {/* Question */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 space-y-4">
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleBack}
                  variant="solid"
                  size="icon"
                  rounded="lg"
                  textColor="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex items-center justify-between flex-1">
                  <Text variant="h3">
                    {t("assignments.openEnded.review.question")}{" "}
                    {currentQuestionIndex + 1}/{currentQuestions?.length || 0}
                  </Text>
                </div>
              </div>
              <Markdown className="prose dark:prose-invert max-w-none">
                {question.body}
              </Markdown>
            </div>

            {/* Student Answer */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 space-y-4">
              <div className="flex items-center justify-between">
                <Text variant="h3">
                  {t("assignments.openEnded.review.studentAnswer")}
                </Text>
                <Text
                  variant="h3"
                  className="text-primary-600 dark:text-primary-400"
                >
                  {currentStudent.studentName} {currentStudent.studentSurname}
                </Text>
              </div>
              {studentAnswer ? (
                <Markdown className="prose dark:prose-invert max-w-none">
                  {studentAnswer}
                </Markdown>
              ) : (
                <Text className="text-gray-500 dark:text-gray-400 italic">
                  {t("assignments.openEnded.review.noAnswer")}
                </Text>
              )}
              <div className="space-y-4">
                <Button
                  onClick={() => setShowAnswers(!showAnswers)}
                  variant="outline"
                  className="w-full justify-between bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 transition-colors duration-200"
                >
                  <div className="flex items-center gap-2">
                    <EyeIcon className="w-5 h-5 text-primary-500" />
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {t("assignments.openEnded.review.correctAnswer")}
                    </span>
                  </div>
                  <ChevronRight
                    className={`w-5 h-5 text-gray-400 transform transition-transform duration-200 ease-in-out ${
                      showAnswers ? "rotate-90" : ""
                    }`}
                  />
                </Button>
              </div>
            </div>
            {showAnswers && (
              <div className="col-span-2 space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                  <Markdown className="prose dark:prose-invert max-w-none">
                    {question.answer}
                  </Markdown>
                </div>

                {question.explanation && (
                  <>
                    <Text variant="h4">
                      {t("assignments.openEnded.review.explanation")}
                    </Text>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
                      <Markdown className="prose dark:prose-invert max-w-none">
                        {question.explanation}
                      </Markdown>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Correct Answer and Explanation (Collapsible) */}

          {/* Grading */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 space-y-2">
            <div className="space-y-2">
              {/* Points slider */}
              <div>
                <div className="flex justify-between mb-2">
                  <Text className="text-sm font-medium">
                    {t("assignments.openEnded.review.points")}
                  </Text>
                  <Text className="text-sm font-medium">
                    {score} / {maxPoints}
                  </Text>
                </div>

                <input
                  type="range"
                  min="0"
                  max={maxPoints}
                  value={score}
                  onChange={(e) => setScore(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                />
              </div>

              {/* Feedback */}
              <div>
                <Text className="text-sm font-medium mb-2">
                  {t("assignments.openEnded.review.feedback")}
                </Text>

                <textarea
                  value={feedback}
                  onChange={handleTextareaChange}
                  onFocus={(e) => adjustTextareaHeight(e.target)}
                  placeholder={t(
                    "assignments.openEnded.review.feedbackPlaceholder",
                  )}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none overflow-y-auto max-h-64"
                  style={{ minHeight: "4.5rem" }}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            <Button
              onClick={handleGradeSubmit}
              variant="solid"
              size="lg"
              rounded="lg"
              disabled={isSubmitting}
              className="bg-primary-500 hover:bg-primary-700 text-white"
            >
              {t("assignments.openEnded.review.saveGrade")}
            </Button>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Modal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={t("assignments.openEnded.review.success.title")}
      >
        <div className="p-6 text-center space-y-6">
          <PartyPopper className="h-16 w-16 mx-auto text-yellow-500 animate-bounce" />
          <Text className="text-lg">
            {t("assignments.openEnded.review.success.messageNoExport")}
          </Text>
          <div className="flex justify-center gap-4">
            <Button
              onClick={() => {
                setShowSuccessModal(false);
                navigate(`/assignments/${assignmentId}`);
              }}
              variant="solid"
              size="md"
              rounded="lg"
              className="bg-primary-500 hover:bg-primary-700 text-white"
            >
              {t("assignments.openEnded.review.success.continue")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OpenEndedQuestionsReviewSequentialView;
